import { useMemo, useState, } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import NewPlus from "@/assets/svg/NewPlus.svg";
import Edit from "@/assets/svg/EditPen.svg";
import TrashCan from "@/assets/svg/TrashCan.svg";
import ColorPicker from "@/assets/svg/Colorpicker.svg";
import styles from "../other/tooltip.module.css";
import { Task } from "../../types/public-types";
import { ProjectDetailType } from "@/api/query/useProjectDetailQuery";
import { UserType } from "@/api/query/useCurrentUserQuery";
import { UserRoleEnumValue } from "@backend/src/schemas/enums";
export const CustomGanttContextMenuContentForProjectTaskView: React.FC<{
  task: Task;
  fontSize: string;
  fontFamily: string;
  userId?: string;
  onEventCall: (data: { action: string; data: Task }) => void;
  onColorChange: (data: Task) => void;
  closeMenu: () => void;
  project: ProjectDetailType | undefined;
  user: UserType | null
}> = ({
  task,
  closeMenu,
  onColorChange,
  onEventCall,
  project, 
  user 
}) => {
  const allowed: ("edit" | "delete" | "addSubTask" | "changeColor")[] = useMemo(() => {
    const allowedActions: ("edit" | "delete" | "addSubTask" | "changeColor")[] = [];
    const assignedUser = project?.assignedUsers.find(
      (u) =>
        u.user.userId == user?.userId
    );
    if (
      user?.userOrganisation[0]?.role == UserRoleEnumValue.ADMINISTRATOR ||
      assignedUser?.projectRole == UserRoleEnumValue.PROJECT_MANAGER
    ) {
      allowedActions.push("edit", "delete", "addSubTask", "changeColor");
    } else  if (
      task &&
      assignedUser?.projectRole == UserRoleEnumValue.TEAM_MEMBER &&
      task.createdByUserId == user?.userId
    ) {
      allowedActions.push("edit", "delete", "addSubTask", "changeColor");
    } else if(
      task && 
      assignedUser?.projectRole == UserRoleEnumValue.TEAM_MEMBER &&
      task.assign.find((taskUser) => taskUser.user.userId == user?.userId)
    ) {
      allowedActions.push("edit", "changeColor");
    }
    return allowedActions;
  }, [
    project?.assignedUsers,
    task,
    user?.userId,
    user?.userOrganisation,
  ]);
  const colors = [
    { id: "red", color: "#ef4444", title: "Red" },
    { id: "pink", color: "#ec4899", title: "Pink" },
    { id: "purple", color: "#a855f7", title: "Purple" },
    { id: "magenta", color: "#ff4dff", title: "Magenta" },
    { id: "violet", color: "#8b5cf6", title: "Violet" },
    { id: "indigo", color: "#6366f1", title: "Indigo" },
    { id: "blue", color: "#3b82f6", title: "Blue" },
    { id: "cyan", color: "#06b6d4", title: "Cyan" },
    { id: "teal", color: "#14b8a6", title: "Teal" },
    { id: "green", color: "#22c55e", title: "Green" },
    { id: "gantt-green", color: "#15803d", title: "Gantt-green" },
    { id: "lime", color: "#84cc16", title: "Lime" },
    { id: "yellow", color: "#eab308", title: "Yellow" },
    { id: "orange", color: "#f97316", title: "Orange" },
    { id: "deep-orange", color: "#ff7043", title: "Deep-orange" },
    { id: "gray", color: "#6b7280", title: "Gray" },
    { id: "light-gray", color: "#d1d5db", title: "Light-gray" },
    { id: "null", color: "#3b82f6", title: "No color", border: true },
  ];
  const [activeColor, setActiveColor] = useState("");
  const handleColorSelect = (color: string) => {
    setActiveColor(color);
    const findColor = colors.find((c) => c.id == color);
    onColorChange({
      ...task,
      styles: {
        backgroundColor: findColor?.color,
        backgroundSelectedColor: findColor?.color,
        progressColor: "#80808088",
        progressSelectedColor: "#80808088",
      },
    });
  };

  if(allowed.length == 0) { 
    return <></>;
  }
  return (
    <div
      className={
        styles.contextMenuList +
        " flex flex-col gap-3 items-center justify-center bg-white shadow-md rounded-md p-2 opacity-80 font-semibold text-gray-700"
      }
      autoFocus
      onBlur={closeMenu}
    >
      {allowed.includes("addSubTask") && <div
        className="flex gap-2 items-center w-full cursor-pointer"
        onClick={() => onEventCall({ action: "createSubTask", data: task })}
      >
        <img src={NewPlus} className={"invert !w-4 !min-w-4"} /> SubTask
      </div>}
      {allowed.includes("edit") && <div
        className="flex gap-2 items-center w-full cursor-pointer"
        onClick={() => onEventCall({ action: "edit", data: task })}
      >
        {" "}
        <img src={Edit} className={" w-4 min-w-4 invert"} />
        Edit
      </div>}
      {allowed.includes("delete") &&   <div
        className="flex gap-2 items-center w-full cursor-pointer"
        onClick={() => onEventCall({ action: "delete", data: task })}
      >
        <img src={TrashCan} className={" w-4 min-w-4"} />
        Delete Task
      </div>}

      {allowed.includes("changeColor") && <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger className="flex gap-1 w-full items-center bg-white">
            <div className="flex gap-2 items-center w-full   ">
              <img src={ColorPicker} className={" w-4 min-w-4 opacity-80"} />
              Color
            </div>
          </TooltipTrigger>
          <TooltipContent side="right">
            <div className="relative overflow-hidden bg-white rounded-md w-64 h-fit ">
              <ul
                className="grid grid-cols-6 gap-2 p-2 overflow-auto"
                role="listbox"
                aria-activedescendant="active-color"
              >
                {colors.map((color) => (
                  <li
                    onClick={() => handleColorSelect(color.id)}
                    key={color.id}
                    className={`flex items-center justify-center w-8 h-8 cursor-pointer rounded-full material-icons ${
                      color.border ? "border border-gray-300" : ""
                    }`}
                    style={{
                      backgroundColor: color.id !== "null" ? color.color : "",
                    }}
                    role="option"
                    aria-selected={color.id === activeColor ? "true" : "false"}
                    data-id={color.id}
                    title={color.title}
                    id={color.id === activeColor ? "active-color" : undefined}
                  >
                    {color.id == "null" ? <i></i> : ""}
                  </li>
                ))}
              </ul>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>}
    </div>
  );
};
